import * as React from 'react';

import { Button, Loading, SearchField, Toolbar } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Grid,
  List,
  Upload,
  FolderPlus,
  FileType2,
  CheckSquare,
  GripVertical,
} from 'lucide-react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import {
  $showUploadDialog,
  $showCreateFolderDialog,
  $templateDialogFileId,
  $showBulkDeleteDialog,
} from '../../_state/fileDialogs.state';
import {
  $viewMode,
  $files,
  $remoteFiles,
  $selectedFileIds,
  $selectAllFiles,
  $isSelectionMode,
  $toggleSelectionMode,
  $searchTerm,
} from '../../_state/files.state';
import { $directoryTree } from '../../_state/fileTree.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FileRecord } from '../../types/files';

import { BulkActionToolbar } from './BulkActionToolbar';
import { FileBreadcrumbs } from './FileBreadcrumbs';
import { FileGrid } from './FileGrid';
import { FileList } from './FileList';
import { FileTree } from './FileTree';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

export const FileExplorer = () => {
  const t = useTranslations();
  const [viewMode, setViewMode] = useAtom($viewMode);
  const files = useAtomValue($files);
  const remoteFiles = useAtomValue($remoteFiles);
  const selectedFileIds = useAtomValue($selectedFileIds);
  const isSelectionMode = useAtomValue($isSelectionMode);
  const setShowUploadDialog = useSetAtom($showUploadDialog);
  const setShowCreateFolderDialog = useSetAtom($showCreateFolderDialog);
  const setTemplateDialogFileId = useSetAtom($templateDialogFileId);
  const setShowBulkDeleteDialog = useSetAtom($showBulkDeleteDialog);
  const selectAllFiles = useSetAtom($selectAllFiles);
  const toggleSelectionMode = useSetAtom($toggleSelectionMode);
  const [searchTerm, setSearchTerm] = useDebouncedAtom($searchTerm, 300);
  const currentView = viewMode || 'grid';
  const { data: directoryTree } = useAtomValue($directoryTree);

  // Calculate optimal tree panel width based on longest directory name
  const calculateOptimalWidth = React.useMemo(() => {
    // Width calculation constants
    const AVG_CHAR_WIDTH_PX = 8;
    const PADDING_AND_ICON_WIDTH_PX = 60;
    const TYPICAL_VIEWPORT_WIDTH_PX = 1440;
    const DEFAULT_WIDTH_PERCENT = 15;
    const MIN_WIDTH_PERCENT = 12;
    const MAX_WIDTH_PERCENT = 25;

    if (!directoryTree || directoryTree.length === 0) {
      return DEFAULT_WIDTH_PERCENT;
    }

    const findLongestName = (nodes: FileRecord[]): number => {
      let maxLength = 0;
      for (const node of nodes) {
        maxLength = Math.max(maxLength, node.name.length);
        if (node.children && node.children.length > 0) {
          maxLength = Math.max(maxLength, findLongestName(node.children));
        }
      }
      return maxLength;
    };

    const longestNameLength = findLongestName(directoryTree);

    // Estimate: ~8px per character + 60px for padding/icon/indentation
    // Convert to percentage: (estimatedWidth / typical screen width) * 100
    // Clamp between MIN and MAX for reasonable defaults
    const estimatedPixelWidth =
      longestNameLength * AVG_CHAR_WIDTH_PX + PADDING_AND_ICON_WIDTH_PX;
    const estimatedPercentage =
      (estimatedPixelWidth / TYPICAL_VIEWPORT_WIDTH_PX) * 100;

    return Math.min(
      Math.max(estimatedPercentage, MIN_WIDTH_PERCENT),
      MAX_WIDTH_PERCENT,
    );
  }, [directoryTree]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+A / Cmd+A - Enable selection mode and select all files
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        selectAllFiles();
      }
      // Escape - Exit selection mode (clears selection)
      else if (e.key === 'Escape' && isSelectionMode) {
        toggleSelectionMode();
      }
      // Delete - Open bulk delete dialog (only if files are selected)
      else if (e.key === 'Delete' && selectedFileIds.size > 0) {
        e.preventDefault();
        setShowBulkDeleteDialog(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectAllFiles,
    toggleSelectionMode,
    isSelectionMode,
    selectedFileIds,
    setShowBulkDeleteDialog,
  ]);

  // Function to check if a file is a supported document type
  const isSupportedDocument = (file: FileRecord): boolean => {
    // Check by file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    switch (fileExtension) {
      case 'docx':
      case 'xlsx':
        return true;
    }

    // Also check by MIME type
    switch (file.mimeType) {
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return true;
      default:
        return false;
    }
  };

  // Check if we can set the selected file as a template
  const canSetAsTemplate = (): boolean => {
    if (selectedFileIds.size !== 1) return false;

    const selectedFileId = [...selectedFileIds][0];
    const selectedFile = files.find((f) => f.id === selectedFileId);

    return selectedFile ? isSupportedDocument(selectedFile) : false;
  };

  const handleSetAsTemplate = () => {
    if (selectedFileIds.size !== 1) return;

    const selectedFileId = [...selectedFileIds][0];
    setTemplateDialogFileId(selectedFileId);
  };

  return (
    <div className="@FileExplorer flex h-full">
      <PanelGroup direction="horizontal" autoSaveId="files-explorer-layout">
        {/* Resizable tree panel */}
        <Panel
          defaultSize={calculateOptimalWidth}
          minSize={12}
          maxSize={35}
          className="border-r border-neutral/20 bg-neutral/5"
        >
          <div className="h-full p-4">
            <FileTree />
          </div>
        </Panel>

        {/* Resize handle */}
        <PanelResizeHandle className="group relative w-1 bg-neutral/10 transition-colors hover:bg-primary/20 active:bg-primary/50">
          <div className="absolute inset-y-0 left-1/2 flex -translate-x-1/2 items-center">
            <GripVertical className="size-4 text-neutral/30 transition-colors group-hover:text-primary/50" />
          </div>
        </PanelResizeHandle>

        {/* Main content panel */}
        <Panel minSize={60}>
          <div className="flex h-full flex-col">
            {/* Toolbar */}
            <Toolbar>
              <div className="flex w-full items-center justify-between">
                <FileBreadcrumbs />

                <div className="flex items-center gap-2">
                  <SearchField
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder={t.fileExplorer.toolbar.searchPlaceholder}
                    aria-label={t.fileExplorer.toolbar.searchAriaLabel}
                    className="w-64"
                  />
                  <Button
                    variant="outline"
                    size="small"
                    onPress={() => setShowUploadDialog(true)}
                    aria-label={t.fileExplorer.toolbar.uploadAriaLabel}
                  >
                    <Upload className="mr-1 size-4" />
                    {t.fileExplorer.toolbar.upload}
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    onPress={() => setShowCreateFolderDialog(true)}
                    aria-label={t.fileExplorer.toolbar.newFolderAriaLabel}
                  >
                    <FolderPlus className="mr-1 size-4" />
                    {t.fileExplorer.toolbar.newFolder}
                  </Button>
                  {canSetAsTemplate() && (
                    <Button
                      variant="outline"
                      size="small"
                      onPress={handleSetAsTemplate}
                      aria-label={t.fileExplorer.toolbar.setAsTemplateAriaLabel}
                    >
                      <FileType2 className="mr-1 size-4" />
                      {t.fileExplorer.toolbar.setAsTemplate}
                    </Button>
                  )}

                  <div className="ml-4 flex items-center gap-1 border-l pl-4">
                    <Button
                      variant={isSelectionMode ? 'solid' : 'outline'}
                      size="small"
                      onPress={() => toggleSelectionMode()}
                      aria-label={
                        isSelectionMode
                          ? t.fileExplorer.toolbar.exitSelectionAriaLabel
                          : t.fileExplorer.toolbar.enterSelectionAriaLabel
                      }
                      aria-pressed={isSelectionMode}
                    >
                      <CheckSquare className="mr-1 size-4" />
                      {isSelectionMode
                        ? t.fileExplorer.toolbar.done
                        : t.fileExplorer.toolbar.select}
                    </Button>
                  </div>

                  <div
                    className="ml-2 flex items-center gap-1 border-l pl-4"
                    role="group"
                    aria-label={t.fileExplorer.toolbar.viewModeAriaLabel}
                  >
                    <Button
                      variant={currentView === 'grid' ? 'solid' : 'outline'}
                      size="small"
                      onPress={() => setViewMode('grid')}
                      aria-label={t.fileExplorer.toolbar.gridView}
                      aria-pressed={currentView === 'grid'}
                    >
                      <Grid className="size-4" />
                    </Button>
                    <Button
                      variant={currentView === 'list' ? 'solid' : 'outline'}
                      size="small"
                      onPress={() => setViewMode('list')}
                      aria-label={t.fileExplorer.toolbar.listView}
                      aria-pressed={currentView === 'list'}
                    >
                      <List className="size-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Toolbar>

            {/* File content */}
            <div className="flex-1 overflow-auto p-4">
              {remoteFiles.isLoading ? (
                <div className="flex h-64 items-center justify-center">
                  <Loading isLoading />
                </div>
              ) : remoteFiles.error ? (
                <div className="flex h-64 items-center justify-center">
                  <p className="text-failure/60">
                    {t.fileExplorer.errorLoading}
                  </p>
                </div>
              ) : currentView === 'grid' ? (
                <FileGrid />
              ) : (
                <FileList />
              )}
            </div>
          </div>
        </Panel>
      </PanelGroup>

      {/* Bulk action toolbar */}
      <BulkActionToolbar />
    </div>
  );
};
